/**
 * Board scene — Survey Says reveal phase.
 *
 * Renders ALL surveyed answers (matched and unmatched), top-rank first.
 * Each row shows:
 *   • the rank (left)
 *   • the surveyed answer text (center-left, large)
 *   • the surveyed count + percentage (center-right; corpus stores `count`
 *     out of 100 surveyed people, so count == percentage)
 *   • the points it awards per matching player (= count * 10)
 *   • an aggregate "N matched" pill (right) — names intentionally NOT shown
 *     here; chat can be busy and listing names becomes unreadable. The side
 *     leaderboard is the canonical place names appear. Unmatched rows show
 *     the chip dimmed.
 *
 * The board container is constrained to MAX_W (~1180px) and lives on the LEFT
 * of the stage so it doesn't overlap the permanent right-side leaderboard.
 *
 * Rows are appended in reveal order (rank ascending) as `setQuestion()` is
 * called with each successive `ss_answer_reveal` accumulated into state.
 */

import type { QuestionState, RevealedAnswer } from '../state/types.js';

export interface BoardHandle {
  el: HTMLDivElement;
  setQuestion(q: QuestionState): void;
  setVisible(v: boolean): void;
}

/**
 * Maximum width of the question/board content area. Stage is 1920×1080;
 * leaderboard panel sits at left=1500, width=380 → content must keep clear
 * of x=1480 with a margin. 1180px leaves a 60px gutter on each side and
 * a 100px gap before the leaderboard. Same value the Who Am I rework uses.
 */
const MAX_W = 1180;

export function createBoard(stage: HTMLElement): BoardHandle {
  const el = document.createElement('div');
  el.setAttribute('data-scene', 'board');
  el.setAttribute('data-testid', 'ss-board');
  el.style.cssText = `
    position: absolute; inset: 0;
    padding: 60px 60px;
    color: var(--sg-text-on-secondary, #FFC43D);
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    pointer-events: none;
  `;

  // Inner container holds the actual content, clamped to MAX_W and pinned
  // to the left of the stage so the right-side leaderboard panel stays clear.
  const inner = document.createElement('div');
  inner.setAttribute('data-testid', 'ss-board-inner');
  inner.style.cssText = `
    width: 100%;
    max-width: ${MAX_W}px;
    height: 100%;
    display: flex; flex-direction: column; gap: 20px;
  `;
  el.appendChild(inner);

  const promptBar = document.createElement('div');
  promptBar.style.cssText = `
    font-size: 40px;
    text-align: center;
    color: var(--sg-primary, #FFC43D);
    letter-spacing: 2px;
    line-height: 1.1;
  `;
  promptBar.setAttribute('data-testid', 'ss-board-prompt');

  const list = document.createElement('div');
  list.setAttribute('data-testid', 'ss-board-list');
  list.style.cssText = `
    flex: 1;
    display: flex; flex-direction: column;
    gap: 12px;
    overflow: hidden;
  `;

  inner.appendChild(promptBar);
  inner.appendChild(list);
  stage.appendChild(el);

  // Track which surveyId+rank combos we've already painted so re-renders
  // (e.g. the store re-emits state on theme changes) don't add duplicate rows
  // and so we can skip the celebratory pop animation on the second pass.
  let lastSurveyId: string | null = null;
  const renderedRanks = new Set<number>();

  return {
    el,
    setQuestion(q) {
      promptBar.textContent = q.prompt;
      // New question -> wipe rows and reset bookkeeping.
      if (q.surveyId !== lastSurveyId) {
        lastSurveyId = q.surveyId;
        list.innerHTML = '';
        renderedRanks.clear();
      }
      // Append any newly revealed rows (idempotent on re-render).
      for (const a of q.revealed) {
        if (renderedRanks.has(a.rank)) continue;
        renderedRanks.add(a.rank);
        list.appendChild(buildRow(a));
      }
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
    },
  };
}

function buildRow(a: RevealedAnswer): HTMLDivElement {
  const matched = a.matchedCount > 0;
  const row = document.createElement('div');
  row.setAttribute('data-testid', `ss-row-rank-${a.rank}`);
  row.setAttribute('data-matched', matched ? '1' : '0');
  row.style.cssText = `
    background: ${matched ? 'var(--sg-tile-bg, #060CE9)' : 'rgba(6,12,233,0.45)'};
    border: 3px solid ${matched ? 'var(--sg-primary, #FFC43D)' : 'rgba(255,196,61,0.45)'};
    border-radius: 14px;
    padding: 12px 20px;
    display: grid;
    grid-template-columns: 56px 1fr 110px auto 150px;
    align-items: center;
    column-gap: 16px;
    transition: background 240ms ease, transform 240ms ease;
    opacity: ${matched ? 1 : 0.78};
  `;

  const num = document.createElement('div');
  num.style.cssText = `
    font-size: 44px;
    text-align: center;
    color: var(--sg-primary, #FFC43D);
  `;
  num.textContent = String(a.rank);

  const ans = document.createElement('div');
  ans.setAttribute('data-testid', `ss-row-answer-${a.rank}`);
  ans.style.cssText = `
    font-size: 36px;
    text-transform: uppercase;
    color: var(--sg-text-on-secondary, #FFC43D);
    letter-spacing: 2px;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `;
  ans.textContent = a.answer;

  // Surveyed count / percentage. Corpus stores count out of 100 surveyed
  // people so count == percentage. Show as "47%" so it reads intuitively.
  const pct = document.createElement('div');
  pct.setAttribute('data-testid', `ss-row-pct-${a.rank}`);
  pct.style.cssText = `
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-size: 30px;
    color: rgba(255,255,255,0.92);
    text-align: right;
    letter-spacing: 1px;
  `;
  pct.textContent = `${a.count}%`;

  const pts = document.createElement('div');
  pts.setAttribute('data-testid', `ss-row-points-${a.rank}`);
  pts.style.cssText = `
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-size: 28px;
    color: var(--sg-celebration, #22C55E);
    min-width: 110px;
    text-align: right;
    letter-spacing: 1px;
    opacity: ${matched ? 1 : 0.55};
  `;
  pts.textContent = `+${a.points} PTS`;

  const matchedCell = document.createElement('div');
  matchedCell.setAttribute('data-testid', `ss-row-matched-${a.rank}`);
  matchedCell.style.cssText = `
    font-family: 'Oswald', sans-serif;
    font-size: 20px;
    color: rgba(255,255,255,0.92);
    text-align: right;
    display: flex;
    justify-content: flex-end;
    overflow: hidden;
  `;
  const matchedChip = document.createElement('span');
  matchedChip.style.cssText = `
    background: ${matched ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.18)'};
    color: ${matched ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.55)'};
    border-radius: 999px;
    padding: 4px 12px;
    letter-spacing: 0.5px;
  `;
  matchedChip.textContent = `${a.matchedCount} matched`;
  matchedCell.appendChild(matchedChip);

  row.appendChild(num);
  row.appendChild(ans);
  row.appendChild(pct);
  row.appendChild(pts);
  row.appendChild(matchedCell);

  if (matched) {
    // Celebratory pop on matched rows only.
    row.style.background = 'var(--sg-celebration, #22C55E)';
    row.style.transform = 'scale(1.02)';
    setTimeout(() => {
      row.style.background = 'var(--sg-tile-bg, #060CE9)';
      row.style.transform = 'scale(1.0)';
    }, 280);
  }

  return row;
}
